/**
 * Engage Dashboard Index
 *
 * Main entry point for Engage tenant admin section.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { api } from '@personic/shared'
import EngageMetricCard from '@/components/engage/EngageMetricCard'
import SessionFeed from '@/components/engage/SessionFeed'
import ConditionChart from '@/components/engage/ConditionChart'
import ConfidenceGauge from '@/components/engage/ConfidenceGauge'

interface DashboardMetrics {
  active_sessions: number
  profiles_enriched_today: number
  personas_generated_today: number
  average_confidence: number
  condition_distribution: Record<string, number>
  recent_sessions: Array<{
    id: string
    status: string
    channel: string
    started_at: string
    message_count: number
    agent_id?: string
  }>
}

export default function EngageDashboard() {
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h')

  const { data: metrics, isLoading, error } = useQuery<DashboardMetrics>({
    queryKey: ['engage-dashboard-metrics', timeRange],
    queryFn: async () => {
      const response = await api.get('/api/engage/v1/metrics/dashboard')
      return response.data
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center"
        >
          <div className="w-12 h-12 border-4 border-engage-primary/20 border-t-engage-primary rounded-full animate-spin" />
          <p className="mt-4 text-gray-500">Loading Engage metrics...</p>
        </motion.div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-lg mb-2">Failed to load metrics</div>
        <p className="text-gray-500">Please try again later</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Engage Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Real-time customer intelligence for your contact center
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as '24h' | '7d' | '30d')}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-engage-primary/20 focus:border-engage-primary"
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
          <Link
            to="/tenant/engage/monitor"
            className="px-4 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium hover:bg-engage-primary/90 transition-colors"
          >
            Live Monitor
          </Link>
        </div>
      </motion.div>

      {/* Metrics Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <EngageMetricCard
          label="Active Sessions"
          value={metrics?.active_sessions || 0}
          icon="🔴"
          trend={null}
          description="Currently live"
          live
        />
        <EngageMetricCard
          label="Profiles Enriched"
          value={metrics?.profiles_enriched_today || 0}
          icon="📊"
          trend={null}
          description="Today"
        />
        <EngageMetricCard
          label="Personas Generated"
          value={metrics?.personas_generated_today || 0}
          icon="🎭"
          trend={null}
          description="Today"
        />
        <EngageMetricCard
          label="Avg. Confidence"
          value={`${Math.round((metrics?.average_confidence || 0) * 100)}%`}
          icon="✓"
          trend={null}
          description="Profile maturity"
        />
      </motion.div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Condition Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Customer Conditions
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Detected emotional states distribution
            </p>
          </div>
          <div className="p-6">
            <ConditionChart data={metrics?.condition_distribution || {}} />
          </div>
        </motion.div>

        {/* Confidence Gauge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Profile Maturity
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Overall profile confidence level
            </p>
          </div>
          <div className="p-6 flex items-center justify-center">
            <ConfidenceGauge value={metrics?.average_confidence || 0} />
          </div>
        </motion.div>
      </div>

      {/* Recent Sessions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card"
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Sessions
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Latest customer interactions
            </p>
          </div>
          <Link
            to="/tenant/engage/monitor"
            className="text-sm text-engage-primary hover:text-engage-primary/80 font-medium"
          >
            View all →
          </Link>
        </div>
        <div className="p-6">
          <SessionFeed sessions={metrics?.recent_sessions || []} />
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="grid md:grid-cols-3 lg:grid-cols-5 gap-6"
      >
        <Link
          to="/tenant/engage/personas"
          className="card p-6 hover:shadow-lg transition-shadow group"
        >
          <div className="text-3xl mb-3">🎭</div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-engage-primary transition-colors">
            Browse Personas
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            View and manage generated customer personas
          </p>
        </Link>

        <Link
          to="/tenant/engage/relationships"
          className="card p-6 hover:shadow-lg transition-shadow group"
        >
          <div className="text-3xl mb-3">🤝</div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-engage-primary transition-colors">
            Relationships
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Agent-customer relationship records
          </p>
        </Link>

        <Link
          to="/tenant/engage/pragmatic"
          className="card p-6 hover:shadow-lg transition-shadow group"
        >
          <div className="text-3xl mb-3">🧠</div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-engage-primary transition-colors">
            Pragmatic Intelligence
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Configure dual-stream analysis and signal sensitivity
          </p>
        </Link>

        <Link
          to="/tenant/engage/coach"
          className="card p-6 hover:shadow-lg transition-shadow group"
        >
          <div className="text-3xl mb-3">🎓</div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-engage-primary transition-colors">
            Coach Lens
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Agent coaching insights and growth tracking
          </p>
        </Link>

        <Link
          to="/tenant/engage/settings"
          className="card p-6 hover:shadow-lg transition-shadow group"
        >
          <div className="text-3xl mb-3">⚙️</div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-engage-primary transition-colors">
            Settings
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Configure Engage presets and webhooks
          </p>
        </Link>
      </motion.div>
    </div>
  )
}
